import React, { useEffect, useState, useCallback } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Row,
  Card,
  CardBody,
  CardHeader,
  CardGroup,
  Button,
  Spinner,
  Alert,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Input,
  Label,
} from "reactstrap";
import { APIClient } from "../../helpers/api_helper";
import { translateApiError } from "../../helpers/translateApiError";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CreateTenantUserModal, {
  CreateTenantUserFormValues,
} from "./CreateTenantUserModal";
import ConfirmActionModal from "../../components/Common/ConfirmActionModal";
import TenantUsageCard from "./TenantUsageCard";
import TenantModal from "../../components/Common/TenantModal";
import { formatDate, formatDateTime } from "../../helpers/dateFormat";
import InfoTip from "../../components/Common/InfoTip";
import TableContainer, {
  SelectFilterOptionGroup,
} from "../../components/Common/TableContainer";
import { Column } from "react-table";
import { encryptData } from "../../helpers/crypto_utils";
import { setAuthorization } from "../../helpers/api_helper";
import { getUserRole, checkUserPermission } from "../Users/api/usersApi";
import chatIcon from "../../assets/images/chat.svg";
import checkIcon from "../../assets/images/check.svg";
import crossIcon from "../../assets/images/cross.svg";
import starIcon from "../../assets/images/star.svg";
import "./styles.css";

const api = new APIClient();

interface TenantDetails {
  id: string;
  type: "onboarding" | "tenant";
  portalId?: string;
  smsSettingsUID?: string;
  name: string;
  email?: string;
  handle?: string;
  status: string;
  inviteStatus?: string;
  tenantStatus?: string;
  isExpired?: boolean;
  expiresAt?: string;
  createdBy?: {
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt?: string;
  smsMetrics: {
    totalSms: number;
    successfulSms: number;
    failedSms: number;
    remainingPoints: number;
  };
  usersCount: number;
}

interface User {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  status: string;
  lastLogin?: string;
  createdAt?: string;
}

const TenantDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const location = useLocation<{ from?: string }>();
  const { t, i18n } = useTranslation();
  document.title = t("common.pageTitle", { page: t("tenants.pageTitle") });

  // Get current user role and permissions
  const currentUserRole = getUserRole();
  const isSuperAdmin = currentUserRole === "SUPER_ADMIN";
  const canCreate = checkUserPermission("create");
  const canDelete = checkUserPermission("delete");
  const canActivate = checkUserPermission("activate");
  const canDeactivate = checkUserPermission("deactivate");
  const canLoginAs = checkUserPermission("loginAs");

  const [tenant, setTenant] = useState<TenantDetails | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [isDeactivatingTenant, setIsDeactivatingTenant] = useState(false);
  const [isActivatingTenant, setIsActivatingTenant] = useState(false);
  const [deactivateReason, setDeactivateReason] = useState("");
  const [createUserError, setCreateUserError] = useState<string | null>(null);
  const [confirmLoginAsUserModal, setConfirmLoginAsUserModal] = useState(false);
  const [selectedUserForLoginAs, setSelectedUserForLoginAs] =
    useState<User | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);

  const [confirmDeleteUserModal, setConfirmDeleteUserModal] = useState(false);
  const [selectedUserForDelete, setSelectedUserForDelete] =
    useState<User | null>(null);
  const [isDeletingUser, setIsDeletingUser] = useState(false);

  const [confirmActivateUserModal, setConfirmActivateUserModal] =
    useState(false);
  const [selectedUserForActivate, setSelectedUserForActivate] =
    useState<User | null>(null);
  const [isActivatingUser, setIsActivatingUser] = useState(false);

  const [confirmDeactivateUserModal, setConfirmDeactivateUserModal] =
    useState(false);
  const [selectedUserForDeactivate, setSelectedUserForDeactivate] =
    useState<User | null>(null);
  const [isDeactivatingUser, setIsDeactivatingUser] = useState(false);

  const [confirmActivateTenantModal, setConfirmActivateTenantModal] =
    useState(false);
  const [confirmDeactivateTenantModal, setConfirmDeactivateTenantModal] =
    useState(false);

  // Pagination and filter states for TableContainer
  const [pageParams, setPageParamsState] = useState({
    rowsPerPage: 10,
    currentPage: 1,
  });
  const [filterParams, setFilterParams] = useState({
    search: "",
    status: "",
  });

  const setPageParams = (params: Partial<typeof pageParams>) => {
    setPageParamsState((prev) => ({ ...prev, ...params }));
  };

  const fetchTenantDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response: any = await api.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/tenants/${id}`
      );
      if (response.data) {
        setTenant(response.data);
      }
    } catch (err: any) {
      console.error("Error fetching tenant details:", err);
      setError(
        err.message ? translateApiError(err) : t("tenants.details.loadFailed")
      );
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  const fetchUsers = useCallback(async () => {
    try {
      const response: any = await api.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/tenants/${id}/users?page=1&take=100`
      );
      if (response.data) {
        setUsers(response.data);
      }
    } catch (err: any) {
      console.error("Error fetching users:", err);
      // Continue with empty users list
      setUsers([]);
    } finally {
      setUsersLoaded(true);
    }
  }, [id]);

  // Filter users based on search term and status filter
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      filterParams.search === "" ||
      user.name.toLowerCase().includes(filterParams.search.toLowerCase()) ||
      user.email.toLowerCase().includes(filterParams.search.toLowerCase()) ||
      user.id.toLowerCase().includes(filterParams.search.toLowerCase()) ||
      user.role.toLowerCase().includes(filterParams.search.toLowerCase());

    const matchesStatus =
      filterParams.status === "" ||
      filterParams.status === "all" ||
      user.status.toLowerCase() === filterParams.status.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  // Calculate pagination
  const totalCount = filteredUsers.length;
  const pageCount = Math.ceil(totalCount / pageParams.rowsPerPage);
  const hasPreviousPage = pageParams.currentPage > 1;
  const hasNextPage = pageParams.currentPage < pageCount;

  useEffect(() => {
    fetchTenantDetails();
    fetchUsers();
  }, [fetchTenantDetails, fetchUsers]);

  const getStatusBadgeClass = (status: string): string => {
    switch (status.toLowerCase()) {
      case "active":
        return "status-badge-active";
      case "inactive":
        return "status-badge-inactive";
      case "pending":
        return "status-badge-pending";
      case "expired":
        return "status-badge-expired";
      default:
        return "status-badge-inactive";
    }
  };

  const getStatusLabel = (status: string): string =>
    t(`common.userStatus.${status.toUpperCase()}`, {
      defaultValue: status.charAt(0).toUpperCase() + status.slice(1),
    });

  const getStatusBadge = (status: string): React.ReactElement => {
    return (
      <span className={`status-badge ${getStatusBadgeClass(status)}`}>
        {getStatusLabel(status)}
      </span>
    );
  };

  // Role mapping for display names
  const getRoleDisplayName = (role?: string): string => {
    if (!role) return t("tenants.details.roleFallback");

    const roleMap: Record<string, string> = {
      SUPER_ADMIN: t("common.roles.SUPER_ADMIN"),
      ADMIN: t("common.roles.ADMIN"),
      TENANT_ADMIN: t("common.roles.TENANT_ADMIN"),
      TENANT_USER: t("common.roles.TENANT_USER"),
    };

    return roleMap[role] || t("tenants.details.roleFallback");
  };

  // Table columns configuration
  const userColumns: Column<User>[] = [
    {
      Header: t("tenants.details.columns.userName"),
      accessor: "name",
      width: "18%",
    },
    {
      Header: t("common.labels.email"),
      accessor: "email",
      width: "26%",
    },
    {
      Header: t("common.labels.role"),
      accessor: "role",
      width: "14%",
      Cell: ({ value }: any) => (
        <span className="badge bg-soft-primary text-primary fs-12">
          {getRoleDisplayName(value)}
        </span>
      ),
    },
    {
      Header: t("tenants.details.columns.accountStatus"),
      accessor: "status",
      width: "14%",
      Cell: ({ value }: any) => getStatusBadge(value),
    },
    {
      // Pending users have never logged in: show when they were invited.
      Header: t("users.columns.lastLogin"),
      id: "lastLogin",
      width: "20%",
      Cell: ({ row }: any) => {
        const user = row.original as User;
        // The backend sets lastLogin when the user is invited, so go by status
        if (user.status?.toUpperCase() !== "PENDING")
          return user.lastLogin
            ? formatDateTime(user.lastLogin, i18n.language)
            : "-";
        if (user.createdAt)
          return (
            <span className="text-muted">
              {t("users.invitedOn", {
                date: formatDate(
                  new Date(user.createdAt).toLocaleDateString("en-CA", {
                    timeZone: "Asia/Taipei",
                  }),
                  i18n.language
                ),
              })}
            </span>
          );
        return "-";
      },
    },
    {
      Header: "",
      id: "action",
      width: "8%",
      Cell: ({ row }: any) => {
        const user = row.original as User;
        const userStatus = user.status.toLowerCase();
        const showResendInvite =
          userStatus === "expired" || userStatus === "pending";
        const showActivate = userStatus === "inactive";
        const showDeactivate = userStatus === "active";
        const customerDisabled = tenant?.status === "inactive";

        // Check if user has any available actions
        const hasAnyAction =
          (checkUserPermission("resendInvite") && showResendInvite) ||
          (canActivate && showActivate) ||
          (canDeactivate && showDeactivate) ||
          canLoginAs ||
          canDelete;

        // If no actions available, don't show dropdown
        if (!hasAnyAction) {
          return null;
        }

        return (
          <div className="d-flex justify-content-end">
            <UncontrolledDropdown>
              <DropdownToggle
                tag="button"
                type="button"
                className="btn btn-row-actions"
                aria-label={t("tenants.details.userActions.action")}
              >
                <i className="ri-more-2-fill fs-16" />
              </DropdownToggle>
              <DropdownMenu end container="body" strategy="fixed">
                {checkUserPermission("resendInvite") && showResendInvite && (
                  <>
                    <DropdownItem
                      onClick={() => handleResendUserInvite(user.id)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <i className="ri-mail-send-line me-2" />
                      {t("tenants.details.userActions.resendInvitation")}
                    </DropdownItem>
                  </>
                )}
                {canLoginAs && (
                  <DropdownItem
                    onClick={() => handleLoginAsUser(user.id, user.name)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <i className="ri-user-shared-line me-2" />
                    {t("tenants.details.userActions.loginAsUser")}
                  </DropdownItem>
                )}
                {canDeactivate && showDeactivate && (
                  <DropdownItem
                    onClick={() => handleDeactivateUser(user.id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                    disabled={customerDisabled}
                  >
                    <i className="ri-pause-circle-line me-2" />
                    {t("tenants.details.userActions.deactivate")}
                  </DropdownItem>
                )}
                {canActivate && showActivate && (
                  <DropdownItem
                    onClick={() => handleActivateUser(user.id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                    disabled={customerDisabled}
                  >
                    <i className="ri-check-line me-2" />
                    {t("tenants.details.userActions.activate")}
                  </DropdownItem>
                )}
                {customerDisabled &&
                  ((canActivate && showActivate) ||
                    (canDeactivate && showDeactivate) ||
                    canDelete) && (
                    <DropdownItem
                      header
                      className="text-wrap"
                      style={{ maxWidth: 240, whiteSpace: "normal" }}
                    >
                      {t("users.actions.customerDisabledHint")}
                    </DropdownItem>
                  )}
                {canDelete && (
                  <DropdownItem
                    onClick={() => handleDeleteUser(user.id)}
                    className={customerDisabled ? undefined : "text-danger"}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      color: customerDisabled ? undefined : "#dc3545",
                    }}
                    disabled={customerDisabled}
                  >
                    <i
                      className="ri-delete-bin-line me-2"
                      style={{ color: customerDisabled ? undefined : "#dc3545" }}
                    />
                    {t("tenants.details.userActions.deleteUser")}
                  </DropdownItem>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
        );
      },
    },
  ];

  // Status filter options
  const statusFilterOptions: SelectFilterOptionGroup[] = [
    {
      options: [
        { label: t("common.labels.all"), value: "all" },
        { label: t("common.userStatus.ACTIVE"), value: "active" },
        { label: t("common.userStatus.INACTIVE"), value: "inactive" },
        { label: t("common.userStatus.PENDING"), value: "pending" },
        { label: t("common.userStatus.EXPIRED"), value: "expired" },
      ],
    },
  ];

  // Back to the page that linked here, otherwise the tenant list.
  const handleBack = () => {
    history.push(location.state?.from || "/customers");
  };

  const handleCreateUser = () => {
    setCreateUserError(null);
    setShowCreateUserModal(true);
  };

  const handleCreateUserSubmit = async (values: CreateTenantUserFormValues) => {
    try {
      setIsCreatingUser(true);
      setCreateUserError(null);

      await api.create(
        `${process.env.REACT_APP_API_BASE_URL}/admin/users`,
        values
      );

      toast.success(t("tenants.details.toast.userCreated"));
      setShowCreateUserModal(false);

      // Refresh the users list
      await fetchUsers();
      await fetchTenantDetails();
    } catch (err: any) {
      console.error("Error creating user:", err);

      // Extract error message from backend response
      let errorMessage = t("tenants.details.toast.createUserFailed");

      if (
        err?.data?.errors &&
        Array.isArray(err.data.errors) &&
        err.data.errors.length > 0
      ) {
        // Use the first error from the errors array
        errorMessage = translateApiError(err.data.errors[0]);
      } else if (err?.data?.message) {
        errorMessage = translateApiError(err.data);
      } else if (err?.message) {
        errorMessage = translateApiError(err);
      }

      setCreateUserError(errorMessage);
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleDeactivateUser = async (userId: string) => {
    const user = users.find((u) => u.id === userId);
    if (user) {
      setSelectedUserForDeactivate(user);
      setConfirmDeactivateUserModal(true);
    }
  };

  const handleConfirmDeactivateUser = async () => {
    if (!selectedUserForDeactivate) return;

    try {
      setIsDeactivatingUser(true);
      await api.update(
        `${process.env.REACT_APP_API_BASE_URL}/admin/users/${selectedUserForDeactivate.id}/deactivate`,
        {}
      );
      toast.success(t("tenants.details.toast.userDeactivated"));
      await fetchUsers();
      setConfirmDeactivateUserModal(false);
      setSelectedUserForDeactivate(null);
    } catch (err: any) {
      console.error("Error deactivating user:", err);
      // API helper already handles error toast and auto logout
    } finally {
      setIsDeactivatingUser(false);
    }
  };

  const handleActivateUser = async (userId: string) => {
    const user = users.find((u) => u.id === userId);
    if (user) {
      setSelectedUserForActivate(user);
      setConfirmActivateUserModal(true);
    }
  };

  const handleConfirmActivateUser = async () => {
    if (!selectedUserForActivate) return;

    try {
      setIsActivatingUser(true);
      await api.update(
        `${process.env.REACT_APP_API_BASE_URL}/admin/users/${selectedUserForActivate.id}/activate`,
        {}
      );
      toast.success(t("tenants.details.toast.userActivated"));
      await fetchUsers();
      setConfirmActivateUserModal(false);
      setSelectedUserForActivate(null);
    } catch (err: any) {
      console.error("Error activating user:", err);
      // API helper already handles error toast and auto logout
    } finally {
      setIsActivatingUser(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    const user = users.find((u) => u.id === userId);
    if (user) {
      setSelectedUserForDelete(user);
      setConfirmDeleteUserModal(true);
    }
  };

  const handleConfirmDeleteUser = async () => {
    if (!selectedUserForDelete) return;

    try {
      setIsDeletingUser(true);
      await api.delete(
        `${process.env.REACT_APP_API_BASE_URL}/admin/users/${selectedUserForDelete.id}`
      );
      toast.success(t("tenants.details.toast.userDeleted"));
      await fetchUsers();
      await fetchTenantDetails();
      setConfirmDeleteUserModal(false);
      setSelectedUserForDelete(null);
    } catch (err: any) {
      console.error("Error deleting user:", err);
      // API helper already handles error toast and auto logout
    } finally {
      setIsDeletingUser(false);
    }
  };

  const handleResendUserInvite = async (userId: string) => {
    try {
      await api.create(
        `${process.env.REACT_APP_API_BASE_URL}/admin/users/${userId}/resend-invite`,
        {}
      );
      toast.success(t("tenants.details.toast.userInviteResent"));
      await fetchUsers();
    } catch (err: any) {
      console.error("Error resending user invitation:", err);
      // API helper already handles error toast and auto logout
    }
  };

  const handleLoginAsUser = async (userId: string, userName: string) => {
    const user = users.find((u) => u.id === userId);
    if (user) {
      setSelectedUserForLoginAs(user);
      setConfirmLoginAsUserModal(true);
    }
  };

  const handleConfirmLoginAsUser = async () => {
    if (!selectedUserForLoginAs) return;

    try {
      setIsImpersonating(true);
      const response: any = await api.create(
        `${process.env.REACT_APP_API_BASE_URL}/admin/users/${selectedUserForLoginAs.id}/impersonate`,
        {}
      );

      if (response?.token && response?.user) {
        // Create auth object matching login saga format
        const authUser = {
          token: response.token,
          userProfile: {
            email: response.user.email,
            first_name: response.user.firstName,
            last_name: response.user.lastName,
          },
          ...(response.tenants &&
            response.tenants.length > 0 && {
              tenants: response.tenants,
              tenant: response.tenants[0], // Primary tenant
            }),
          roles: response.user.roles,
          isSuperAdmin: response.user.roles?.includes("SUPER_ADMIN") || false,
          isAdmin:
            response.user.roles?.includes("ADMIN") ||
            response.user.roles?.includes("SUPER_ADMIN") ||
            false,
          isTenant:
            response.user.roles?.includes("TENANT_ADMIN") ||
            response.user.roles?.includes("TENANT_USER") ||
            false,
        };

        // Clear old auth data
        localStorage.removeItem("authUser");
        sessionStorage.removeItem("authUser");

        // Store encrypted auth data in localStorage
        localStorage.setItem("authUser", encryptData(authUser));

        // Set authorization header
        setAuthorization(response.token);

        // Force full page reload to refresh all auth state
        window.location.href = "/dashboard";
      }
    } catch (err: any) {
      console.error("Error impersonating user:", err);
      // Toast is handled by api_helper interceptor
    } finally {
      setIsImpersonating(false);
      setConfirmLoginAsUserModal(false);
    }
  };

  const handleDeactivate = () => {
    if (!tenant) return;

    // Don't deactivate if already inactive
    if (tenant.status === "inactive") {
      toast.info(t("tenants.details.toast.tenantAlreadyInactive"));
      return;
    }

    // Only deactivate actual tenants, not onboarding records
    if (tenant.type !== "tenant") {
      toast.error(t("tenants.details.toast.cannotDeactivateOnboarding"));
      return;
    }

    setDeactivateReason("");
    setConfirmDeactivateTenantModal(true);
  };

  const handleConfirmDeactivateTenant = async () => {
    if (!tenant) return;

    try {
      setIsDeactivatingTenant(true);

      await api.update(
        `${process.env.REACT_APP_API_BASE_URL}/admin/tenants/${id}/status`,
        {
          action: "deactivate",
          ...(deactivateReason.trim() && { reason: deactivateReason.trim() }),
        }
      );

      toast.success(t("tenants.toast.tenantDeactivated"));

      // Refresh tenant details
      await fetchTenantDetails();
      setConfirmDeactivateTenantModal(false);
    } catch (err: any) {
      console.error("Error deactivating tenant:", err);
      // API helper already handles error toast and auto logout
    } finally {
      setIsDeactivatingTenant(false);
    }
  };

  const handleActivate = () => {
    if (!tenant) return;

    // Don't activate if already active
    if (tenant.status === "active") {
      toast.info(t("tenants.details.toast.tenantAlreadyActive"));
      return;
    }

    // Only activate actual tenants, not onboarding records
    if (tenant.type !== "tenant") {
      toast.error(t("tenants.details.toast.cannotActivateOnboarding"));
      return;
    }

    setConfirmActivateTenantModal(true);
  };

  const handleConfirmActivateTenant = async () => {
    if (!tenant) return;

    try {
      setIsActivatingTenant(true);

      await api.update(
        `${process.env.REACT_APP_API_BASE_URL}/admin/tenants/${id}/status`,
        { action: "activate" }
      );

      toast.success(t("tenants.toast.tenantActivated"));

      // Refresh tenant details
      await fetchTenantDetails();
      setConfirmActivateTenantModal(false);
    } catch (err: any) {
      console.error("Error activating tenant:", err);
      // API helper already handles error toast and auto logout
    } finally {
      setIsActivatingTenant(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center mt-5">
            <Spinner color="primary" />
            <p className="mt-2">{t("tenants.details.loading")}</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger">
            {error || t("tenants.details.notFound")}
            <Button color="link" onClick={handleBack} className="ms-2">
              {t("tenants.details.backToList")}
            </Button>
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content tenant-details-page">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Container fluid>
        {/* Create Tenant User Modal */}
        <CreateTenantUserModal
          show={showCreateUserModal}
          onClose={() => {
            setShowCreateUserModal(false);
            setCreateUserError(null);
          }}
          onSubmit={handleCreateUserSubmit}
          isLoading={isCreatingUser}
          error={createUserError}
          tenantId={id}
        />

        {/* Confirm Login As User Modal */}
        <ConfirmActionModal
          show={confirmLoginAsUserModal}
          onConfirm={handleConfirmLoginAsUser}
          onClose={() => setConfirmLoginAsUserModal(false)}
          title={t("tenants.details.confirmLoginAs.title")}
          message={t("tenants.details.confirmLoginAs.message")}
          confirmText={t("tenants.details.confirmLoginAs.confirm")}
          confirmColor="primary"
          isLoading={isImpersonating}
          user={
            selectedUserForLoginAs
              ? {
                  firstName: selectedUserForLoginAs.firstName || "",
                  lastName: selectedUserForLoginAs.lastName || "",
                  email: selectedUserForLoginAs.email,
                }
              : undefined
          }
          tenant={undefined}
        />

        {/* Confirm Delete User Modal */}
        <ConfirmActionModal
          show={confirmDeleteUserModal}
          onConfirm={handleConfirmDeleteUser}
          onClose={() => setConfirmDeleteUserModal(false)}
          title={t("tenants.details.confirmDeleteUser.title")}
          message={t("tenants.details.confirmDeleteUser.message")}
          confirmText={t("tenants.details.confirmDeleteUser.confirm")}
          confirmColor="danger"
          isLoading={isDeletingUser}
          user={
            selectedUserForDelete
              ? {
                  firstName: selectedUserForDelete.firstName || "",
                  lastName: selectedUserForDelete.lastName || "",
                  email: selectedUserForDelete.email,
                }
              : undefined
          }
          tenant={undefined}
        />

        {/* Confirm Activate User Modal */}
        <ConfirmActionModal
          show={confirmActivateUserModal}
          onConfirm={handleConfirmActivateUser}
          onClose={() => setConfirmActivateUserModal(false)}
          title={t("tenants.details.confirmActivateUser.title")}
          message={t("tenants.details.confirmActivateUser.message")}
          confirmText={t("tenants.details.confirmActivateUser.confirm")}
          confirmColor="success"
          isLoading={isActivatingUser}
          user={
            selectedUserForActivate
              ? {
                  firstName: selectedUserForActivate.firstName || "",
                  lastName: selectedUserForActivate.lastName || "",
                  email: selectedUserForActivate.email,
                }
              : undefined
          }
          tenant={undefined}
        />

        {/* Confirm Deactivate User Modal */}
        <ConfirmActionModal
          show={confirmDeactivateUserModal}
          onConfirm={handleConfirmDeactivateUser}
          onClose={() => setConfirmDeactivateUserModal(false)}
          title={t("tenants.details.confirmDeactivateUser.title")}
          message={t("tenants.details.confirmDeactivateUser.message")}
          confirmText={t("tenants.details.confirmDeactivateUser.confirm")}
          confirmColor="danger"
          isLoading={isDeactivatingUser}
          user={
            selectedUserForDeactivate
              ? {
                  firstName: selectedUserForDeactivate.firstName || "",
                  lastName: selectedUserForDeactivate.lastName || "",
                  email: selectedUserForDeactivate.email,
                }
              : undefined
          }
          tenant={undefined}
        />

        {/* Confirm Activate Tenant Modal */}
        <ConfirmActionModal
          show={confirmActivateTenantModal}
          onConfirm={handleConfirmActivateTenant}
          onClose={() => setConfirmActivateTenantModal(false)}
          title={t("tenants.details.confirmActivateTenant.title")}
          message={t("tenants.details.confirmActivateTenant.message")}
          confirmText={t("tenants.details.confirmActivateTenant.confirm")}
          confirmColor="success"
          isLoading={isActivatingTenant}
          user={undefined}
          tenant={
            tenant
              ? {
                  name: tenant.name,
                  email: tenant.email || "",
                }
              : undefined
          }
        />

        {/* Confirm Deactivate Tenant Modal */}
        <ConfirmActionModal
          show={confirmDeactivateTenantModal}
          onConfirm={handleConfirmDeactivateTenant}
          onClose={() => setConfirmDeactivateTenantModal(false)}
          title={t("tenants.details.confirmDeactivateTenant.title")}
          message={t("tenants.details.confirmDeactivateTenant.message")}
          confirmText={t("tenants.details.confirmDeactivateTenant.confirm")}
          confirmColor="danger"
          isLoading={isDeactivatingTenant}
          user={undefined}
          tenant={
            tenant
              ? {
                  name: tenant.name,
                  email: tenant.email || "",
                }
              : undefined
          }
        >
          <div className="mb-3 text-start">
            <Label htmlFor="deactivate-reason" className="form-label">
              {t("tenants.details.confirmDeactivateTenant.reasonLabel")}
            </Label>
            <Input
              id="deactivate-reason"
              type="textarea"
              rows={2}
              maxLength={500}
              value={deactivateReason}
              onChange={(e) => setDeactivateReason(e.target.value)}
              placeholder={t(
                "tenants.details.confirmDeactivateTenant.reasonPlaceholder"
              )}
            />
          </div>
        </ConfirmActionModal>

        <TenantModal
          show={editingName}
          onCloseClick={() => {
            setEditingName(false);
            fetchTenantDetails();
          }}
          mode="edit"
          existingData={{ tenantName: tenant.name }}
          tenantId={tenant.id}
        />

        {/* Back Button */}
        <div className="mb-3">
          <Button color="link" onClick={handleBack} className="back-button p-0">
            <i className="ri-arrow-left-line me-2"></i>
            {t("common.actions.back")}
          </Button>
        </div>

        {/* Page Header */}
        <div className="page-header d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center gap-2">
            <h2 className="page-title mb-0">{tenant.name}</h2>
            <span
              className={`status-badge ${getStatusBadgeClass(tenant.status)}`}
            >
              {getStatusLabel(tenant.status)}
            </span>
          </div>
          <div className="d-flex gap-2">
            {isSuperAdmin && tenant.status === "inactive" ? (
              <Button
                color="success"
                onClick={handleActivate}
                className="btn-activate"
                disabled={isActivatingTenant || tenant.type !== "tenant"}
              >
                {isActivatingTenant ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {t("tenants.details.activating")}
                  </>
                ) : (
                  <>
                    <i className="ri-check-line me-2"></i>
                    {t("tenants.details.activate")}
                  </>
                )}
              </Button>
            ) : null}
            {/* Rare SUPER_ADMIN actions, kept out of the title row */}
            {isSuperAdmin && (
              <UncontrolledDropdown>
                <DropdownToggle
                  tag="button"
                  className="btn btn-more-actions"
                  title={t("tenants.details.moreActions")}
                  aria-label={t("tenants.details.moreActions")}
                >
                  {isDeactivatingTenant ? (
                    <Spinner size="sm" />
                  ) : (
                    <i className="ri-more-2-fill"></i>
                  )}
                </DropdownToggle>
                <DropdownMenu end>
                  <DropdownItem
                    className="d-flex align-items-center"
                    onClick={() => setEditingName(true)}
                  >
                    <i className="ri-pencil-line me-2 text-muted"></i>
                    {t("tenants.details.editName")}
                  </DropdownItem>
                  {tenant.status === "active" && (
                    <DropdownItem
                      className="d-flex align-items-center text-danger"
                      onClick={handleDeactivate}
                      disabled={
                        isDeactivatingTenant || tenant.type !== "tenant"
                      }
                    >
                      <i className="ri-forbid-line me-2"></i>
                      {t("tenants.details.deactivate")}
                    </DropdownItem>
                  )}
                </DropdownMenu>
              </UncontrolledDropdown>
            )}
          </div>
        </div>

        {/* Basic info, moved here from the tenant list */}
        <div className="d-flex flex-wrap gap-4 mb-4 text-muted">
          {[
            [t("tenants.details.info.tenantId"), tenant.id],
            [t("tenants.details.info.portalId"), tenant.portalId],
            [t("tenants.details.info.every8dAccount"), tenant.smsSettingsUID],
            [
              t("tenants.details.info.createdAt"),
              tenant.createdAt &&
                formatDateTime(tenant.createdAt, i18n.language),
            ],
          ].map(([label, value]) => (
            <div key={label}>
              {/* A real space, so copied text reads "客戶 ID <id>" */}
              {label}{" "}
              {label === t("tenants.details.info.portalId") && value ? (
                // The HubSpot account ID opens that account in HubSpot.
                <a
                  href={`https://app.hubspot.com/contacts/${value}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fw-medium ms-1"
                  title={t("tenants.details.openInHubSpot")}
                >
                  {value}
                </a>
              ) : (
                <span className="text-body fw-medium ms-1">{value || "-"}</span>
              )}
            </div>
          ))}
        </div>

        {/* SMS Metrics */}
        <CardGroup className="stats-card-wrapper my-2 gap-3 mb-4">
          <Card className="rounded">
            <CardBody>
              <Row>
                <div className="d-flex flex-wrap gap-3">
                  <div className="flex-1">
                    <p>{t("tenants.details.metrics.allSms")}</p>
                  </div>
                  <div className="stats-card-img-wrapper bg-primary">
                    <img src={chatIcon} alt="" />
                  </div>
                </div>
              </Row>
              <h3>{tenant.smsMetrics.totalSms.toLocaleString()}</h3>
            </CardBody>
          </Card>
          <Card className="rounded">
            <CardBody>
              <Row>
                <div className="d-flex">
                  <div className="flex-1">
                    <p>{t("tenants.details.metrics.successfulSms")}</p>
                  </div>
                  <div className="stats-card-img-wrapper bg-success">
                    <img src={checkIcon} alt="" />
                  </div>
                </div>
              </Row>
              <h3>{tenant.smsMetrics.successfulSms.toLocaleString()}</h3>
            </CardBody>
          </Card>
          <Card className="rounded">
            <CardBody>
              <Row>
                <div className="d-flex">
                  <div className="flex-1">
                    <p>{t("tenants.details.metrics.failedSms")}</p>
                  </div>
                  <div className="stats-card-img-wrapper bg-danger">
                    <img src={crossIcon} alt="" />
                  </div>
                </div>
              </Row>
              <h3>{tenant.smsMetrics.failedSms.toLocaleString()}</h3>
            </CardBody>
          </Card>
          <Card className="rounded">
            <CardBody>
              <Row>
                <div className="d-flex">
                  <div className="flex-1">
                    <p>
                      {t("tenants.details.metrics.remainingPoints")}
                      <InfoTip text={t("dashboard.cards.remainingPointsHint")} />
                    </p>
                  </div>
                  <div className="stats-card-img-wrapper bg-info">
                    <img src={starIcon} alt="" />
                  </div>
                </div>
              </Row>
              <h3>{tenant.smsMetrics.remainingPoints.toLocaleString()}</h3>
            </CardBody>
          </Card>
        </CardGroup>

        {/* Plan, usage, last 6 months, pause / resume */}
        {tenant.type === "tenant" && <TenantUsageCard tenant={tenant} />}

        {/* Users Table */}
        <Card>
          <CardHeader className="d-flex align-items-center justify-content-between">
            <h5 className="card-title mb-0">
              {t("tenants.details.usersHeading")}
            </h5>
            {/* Next to the list it adds to */}
            {canCreate && (
              <Button
                color="primary"
                outline
                onClick={handleCreateUser}
                className="btn-card-action"
              >
                <i className="ri-user-add-line me-2"></i>
                {t("tenants.details.createUser")}
              </Button>
            )}
          </CardHeader>
          <CardBody>
            {usersLoaded && users.length === 0 ? (
              <p className="text-muted mb-0">{t("tenants.details.noUsers")}</p>
            ) : (
              <TableContainer
                columns={userColumns}
                data={filteredUsers}
                loading={false}
                pageCount={pageCount}
                hasPreviousPage={hasPreviousPage}
                hasNextPage={hasNextPage}
                pageParams={pageParams}
                setPageParams={setPageParams}
                filterParams={filterParams}
                setFilterParams={setFilterParams}
                disableRowModal={true}
                datePicker={false}
                selectFilterOptions={statusFilterOptions}
                isGlobalFilter={true}
                isGlobalSearch={true}
                tableClass="custom-header-css"
                theadClass="text-muted"
              />
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default TenantDetailsPage;
